import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';

const siteUrl = process.env.REACT_APP_SITE_URL || 'https://latchiya.com';

const MAP_EMBED_SRC =
  'https://maps.google.com/maps?q=Divyasree+Elan%2C+Bellandur+Gate%2C+Sarjapur+Main+Road%2C+Bangalore&t=&z=15&ie=UTF8&iwloc=&output=embed';

const Contact = () => {
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success') === '1';

  const formSubmitNext = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/contact?success=1`;
  }, []);

  return (
    <>
      <SEOHead
        title="Contact"
        description="Contact Latchiya Software Systems in Bangalore. Send a message to support@latchiya.com or visit our office."
        url={`${siteUrl}/contact`}
      />
      <div className="container-custom py-12 lg:py-16">
        <header className="max-w-2xl mb-10">
          <p className="eyebrow mb-3">Contact us</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">
            Reach the Latchiya team
          </h1>
          <p className="mt-4 text-gray-400 leading-relaxed">
            Use the form to email{' '}
            <a
              href="mailto:support@latchiya.com"
              className="text-primary-300 hover:text-primary-200"
            >
              support@latchiya.com
            </a>
            . For product pilots (Documint, m-REACH, eClassroomz, IDGate, Scan4Loyalty) or
            consulting
            engagements, include your organization and use case.
          </p>
        </header>

        {success && (
          <div
            className="mb-8 rounded-xl border border-primary-500/30 bg-primary-900/25 px-4 py-3 text-sm text-primary-100"
            role="status"
          >
            Thank you—your message was sent. We will get back to you shortly.
          </div>
        )}

        <div className="grid gap-10 lg:grid-cols-2 lg:gap-12 items-start">
          <div className="glass-panel rounded-2xl p-6 lg:p-8">
            <h2 className="font-display text-lg font-semibold text-white mb-6">
              Send a message
            </h2>
            <form
              action="https://formsubmit.co/support@latchiya.com"
              method="POST"
              className="space-y-4"
            >
              <input type="hidden" name="_subject" value="Latchiya.com contact form" />
              <input type="hidden" name="_template" value="table" />
              {formSubmitNext ? (
                <input type="hidden" name="_next" value={formSubmitNext} />
              ) : null}
              <input type="hidden" name="_captcha" value="false" />

              <div>
                <label htmlFor="contact-name" className="sr-only">
                  Name
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder="Your name"
                  className="w-full rounded-lg border border-white/10 bg-surface-200/60 px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="sr-only">
                  Email
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="Work email"
                  className="w-full rounded-lg border border-white/10 bg-surface-200/60 px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="sr-only">
                  Phone
                </label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  placeholder="Phone (optional)"
                  className="w-full rounded-lg border border-white/10 bg-surface-200/60 px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="sr-only">
                  Message
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  rows={5}
                  placeholder="How can we help? (product pilot, consulting, partnership…)"
                  className="w-full rounded-lg border border-white/10 bg-surface-200/60 px-4 py-3 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y min-h-[120px]"
                />
              </div>
              <button type="submit" className="btn-primary w-full sm:w-auto">
                Send email
              </button>
            </form>
            <p className="mt-4 text-xs text-gray-500 leading-relaxed">
              Submissions are delivered to support@latchiya.com. First-time use
              may require confirming the forwarding address in your inbox.
            </p>
          </div>

          <div>
            <h2 className="font-display text-lg font-semibold text-white mb-4">
              Office locations
            </h2>
            <div className="space-y-6 mb-6">
              <address className="not-italic text-sm text-gray-400 leading-relaxed">
                <strong className="text-gray-300 block mb-2">Office</strong>
                <span className="text-gray-500 block text-xs uppercase tracking-wide mb-1">
                  Bangalore
                </span>
                Latchiya Software Systems Pvt Ltd
                <br />
                1F-801, Divyasree Elan, Bellandur Gate,
                <br />
                Sarjapur Main Road, Bangalore — 560035, Karnataka, India
              </address>
              <address className="not-italic text-sm text-gray-400 leading-relaxed">
                <strong className="text-gray-300 block mb-2">
                  Registered office
                </strong>
                Latchiya Software Systems Pvt Ltd
                <br />
                4-21-35/1, Park Town I Street,
                <br />
                {'P & T Nagar'}
                <br />
                Madurai — 625017, Tamil Nadu, India
              </address>
            </div>
            <p className="text-sm text-gray-400 mb-2">
              <span className="text-gray-500">Phone:</span>{' '}
              <a
                href="tel:+919945101015"
                className="text-primary-300 hover:text-primary-200"
              >
                +91 99451 01015
              </a>
            </p>
            <p className="text-sm text-gray-400 mb-8">
              <span className="text-gray-500">Email:</span>{' '}
              <a
                href="mailto:support@latchiya.com"
                className="text-primary-300 hover:text-primary-200"
              >
                support@latchiya.com
              </a>
            </p>

            <div className="rounded-xl overflow-hidden border border-white/10 aspect-[4/3] bg-surface-200/40">
              <iframe
                title="Latchiya office location on map"
                src={MAP_EMBED_SRC}
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: '280px' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
